import React, { useState, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useInView } from 'framer-motion';

const EMAIL = 'info@webfacilcastellon.es';

const PLANES = [
  { id: 'estatica', label: 'Web Estática', desc: 'Para autónomos y pequeños negocios', color: '#2e8bc0', icon: 'fa-solid fa-display' },
  { id: 'mediana',  label: 'Web Mediana',  desc: 'Para empresas en crecimiento',      color: '#f0ad4e', icon: 'fa-solid fa-bolt' },
  { id: 'tienda',   label: 'Tienda Online', desc: 'Comercio electrónico completo',     color: '#5cb85c', icon: 'fa-solid fa-cart-shopping' },
];

const EXTRAS = [
  { id: 'seo',      label: 'SEO Avanzado',          desc: 'Posicionamiento en buscadores' },
  { id: 'blog',     label: 'Blog / Noticias',         desc: 'Sistema de publicación de artículos' },
  { id: 'multi',    label: 'Multiidioma',             desc: 'Castellano, valenciano e inglés' },
  { id: 'reservas', label: 'Sistema de reservas',     desc: 'Citas online integradas' },
  { id: 'chat',     label: 'Chat en vivo',            desc: 'Atención al cliente en tiempo real' },
  { id: 'mante',    label: 'Mantenimiento mensual',   desc: 'Actualizaciones y soporte continuo' },
];

const Precios = () => {
  const [planSel, setPlanSel] = useState('estatica');
  const [extrasSel, setExtrasSel] = useState([]);
  const [contacto, setContacto] = useState({ nombre: '', email: '', telefono: '' });
  const [mensaje, setMensaje] = useState('');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const toggleExtra = (id) =>
    setExtrasSel(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);

  const planLabel = PLANES.find(p => p.id === planSel)?.label || '';
  const extrasLabels = extrasSel.map(id => EXTRAS.find(e => e.id === id)?.label).filter(Boolean);

  const buildMailto = () => {
    const subject = encodeURIComponent('Solicitud de presupuesto — Web Fácil Castellón');
    const serviciosLine = extrasLabels.length
      ? `\nServicios adicionales: ${extrasLabels.join(', ')}`
      : '';
    const contactoLine = [
      contacto.nombre  ? `Nombre: ${contacto.nombre}`   : '',
      contacto.email   ? `Email: ${contacto.email}`     : '',
      contacto.telefono ? `Teléfono: ${contacto.telefono}` : '',
    ].filter(Boolean).join('\n');
    const body = encodeURIComponent(
      `Hola,\n\n${contactoLine ? `${contactoLine}\n\n` : ''}Me gustaría recibir información sobre:\n\nTipo de web: ${planLabel}${serviciosLine}\n\n${mensaje ? `Mensaje:\n${mensaje}\n\n` : ''}Gracias.`
    );
    return `mailto:${EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleEnviar = () => {
    window.location.href = buildMailto();
  };

  return (
    <section id="precios" style={{ background: 'var(--bg-surface)' }}>
      <Container>
        <div className="text-center mb-5" ref={ref}>
          <motion.span className="badge-primary mb-3 d-inline-block" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}>
            Solicita tu presupuesto
          </motion.span>
          <motion.h2 className="section-title" initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.1 }}>
            ¿Qué necesitas para tu proyecto?
          </motion.h2>
          <div className="accent-line center" />
          <motion.p className="section-subtitle" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.2 }}>
            Selecciona el tipo de web y los servicios que te interesan. Te prepararemos un presupuesto a medida.
          </motion.p>
        </div>

        <Row className="g-4 justify-content-center">
          <Col lg={8}>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3 }}>

              {/* Paso 1: Tipo de web */}
              <div className="card-dark mb-4">
                <h4 style={{ color: 'white', fontWeight: 700, marginBottom: '1.2rem', fontSize: '0.95rem', fontFamily: 'var(--font-heading)' }}>
                  <span style={{ color: '#2e8bc0', marginRight: '10px' }}>01</span>
                  Tipo de web
                </h4>
                <div className="d-flex flex-column gap-2">
                  {PLANES.map(({ id, label, desc, color, icon }) => {
                    const active = planSel === id;
                    return (
                      <div
                        key={id}
                        onClick={() => setPlanSel(id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '14px',
                          padding: '14px 18px', borderRadius: '10px', cursor: 'pointer',
                          border: `2px solid ${active ? color : 'rgba(255,255,255,0.07)'}`,
                          background: active ? `${color}10` : 'transparent',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%',
                          border: `2px solid ${active ? color : '#445568'}`,
                          background: active ? color : 'transparent',
                          flexShrink: 0, transition: 'all 0.2s',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {active && <i className="fa-solid fa-check" style={{ color: 'white', fontSize: '0.55rem' }} />}
                        </div>
                        <i className={icon} style={{ color: active ? color : '#6a86a8', fontSize: '1rem', width: '18px' }} />
                        <div>
                          <div style={{ color: active ? 'white' : '#c8d8ec', fontWeight: 600, fontSize: '0.92rem' }}>{label}</div>
                          <div style={{ color: '#6a86a8', fontSize: '0.78rem' }}>{desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Paso 2: Datos de contacto */}
              <div className="card-dark mb-4">
                <h4 style={{ color: 'white', fontWeight: 700, marginBottom: '1.2rem', fontSize: '0.95rem', fontFamily: 'var(--font-heading)' }}>
                  <span style={{ color: '#2e8bc0', marginRight: '10px' }}>02</span>
                  Tus datos de contacto
                </h4>
                <Row className="g-3">
                  <Col md={6}>
                    <label style={{ color: '#8fa3bf', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Nombre *</label>
                    <input
                      type="text" required placeholder="Tu nombre"
                      value={contacto.nombre}
                      onChange={e => setContacto(prev => ({ ...prev, nombre: e.target.value }))}
                      style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '13px 16px', color: '#e8edf5', fontSize: '0.93rem', outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s' }}
                      onFocus={e => e.target.style.borderColor = 'rgba(46,139,192,0.7)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    />
                  </Col>
                  <Col md={6}>
                    <label style={{ color: '#8fa3bf', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Email *</label>
                    <input
                      type="email" required placeholder="tu@email.com"
                      value={contacto.email}
                      onChange={e => setContacto(prev => ({ ...prev, email: e.target.value }))}
                      style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '13px 16px', color: '#e8edf5', fontSize: '0.93rem', outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s' }}
                      onFocus={e => e.target.style.borderColor = 'rgba(46,139,192,0.7)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    />
                  </Col>
                  <Col md={6}>
                    <label style={{ color: '#8fa3bf', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Teléfono</label>
                    <input
                      type="tel" placeholder="621 000 000"
                      value={contacto.telefono}
                      onChange={e => setContacto(prev => ({ ...prev, telefono: e.target.value }))}
                      style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '13px 16px', color: '#e8edf5', fontSize: '0.93rem', outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s' }}
                      onFocus={e => e.target.style.borderColor = 'rgba(46,139,192,0.7)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    />
                  </Col>
                </Row>
              </div>

              {/* Paso 3: Extras */}
              <div className="card-dark mb-4">
                <h4 style={{ color: 'white', fontWeight: 700, marginBottom: '1.2rem', fontSize: '0.95rem', fontFamily: 'var(--font-heading)' }}>
                  <span style={{ color: '#2e8bc0', marginRight: '10px' }}>03</span>
                  Servicios adicionales <span style={{ color: '#6a86a8', fontWeight: 400, fontSize: '0.8rem' }}>(opcional)</span>
                </h4>
                <Row className="g-2">
                  {EXTRAS.map(({ id, label, desc }) => {
                    const active = extrasSel.includes(id);
                    return (
                      <Col md={6} key={id}>
                        <div
                          onClick={() => toggleExtra(id)}
                          style={{
                            padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                            border: `2px solid ${active ? '#2e8bc0' : 'rgba(255,255,255,0.07)'}`,
                            background: active ? 'rgba(46,139,192,0.1)' : 'transparent',
                            transition: 'all 0.2s',
                            display: 'flex', alignItems: 'flex-start', gap: '10px',
                          }}
                        >
                          <div style={{
                            width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0, marginTop: '2px',
                            border: `2px solid ${active ? '#2e8bc0' : '#445568'}`,
                            background: active ? '#2e8bc0' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
                          }}>
                            {active && <i className="fa-solid fa-check" style={{ color: 'white', fontSize: '0.55rem' }} />}
                          </div>
                          <div>
                            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: active ? 'white' : '#c8d8ec', marginBottom: '2px' }}>
                              {label}
                            </div>
                            <div style={{ fontSize: '0.76rem', color: '#6a86a8' }}>{desc}</div>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>

              {/* Paso 4: Mensaje */}
              <div className="card-dark mb-4">
                <h4 style={{ color: 'white', fontWeight: 700, marginBottom: '1.2rem', fontSize: '0.95rem', fontFamily: 'var(--font-heading)' }}>
                  <span style={{ color: '#2e8bc0', marginRight: '10px' }}>04</span>
                  Cuéntanos algo más <span style={{ color: '#6a86a8', fontWeight: 400, fontSize: '0.8rem' }}>(opcional)</span>
                </h4>
                <textarea
                  value={mensaje}
                  onChange={e => setMensaje(e.target.value)}
                  rows={4}
                  placeholder="Describe tu negocio, qué quieres conseguir con la web, referencias que te gusten..."
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    padding: '13px 16px',
                    color: '#e8edf5',
                    fontSize: '0.93rem',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '110px',
                    fontFamily: 'var(--font-body)',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = 'rgba(46,139,192,0.6)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>

              {/* Resumen + CTA */}
              <div style={{
                background: 'rgba(46,139,192,0.07)',
                border: '1px solid rgba(46,139,192,0.25)',
                borderRadius: 'var(--radius)',
                padding: '1.4rem 1.6rem',
                display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
              }}>
                <div>
                  <div style={{ color: '#8fa3bf', fontSize: '0.8rem', marginBottom: '4px' }}>Tu selección:</div>
                  <div style={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
                    {planLabel}
                    {extrasLabels.length > 0 && (
                      <span style={{ color: '#45a8e0' }}> · {extrasLabels.join(' · ')}</span>
                    )}
                  </div>
                </div>
                <button className="btn-accent-custom" onClick={handleEnviar} style={{ padding: '13px 30px' }}>
                  Solicitar presupuesto
                  <i className="fa-solid fa-paper-plane ms-2" style={{ fontSize: '0.82rem' }} />
                </button>
              </div>

              <p style={{ color: '#445568', fontSize: '0.78rem', marginTop: '12px', textAlign: 'center' }}>
                <i className="fa-solid fa-lock me-1" />
                Tus datos son confidenciales y no serán cedidos a terceros. Te respondemos en menos de 24 horas.
              </p>

            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Precios;
